/**
 * Product Customizer Module
 * Handles the product customizer modal functionality
 * @version 1.0.0
 */

class ProductCustomizer {
    constructor() {
        this.modal = null;
        this.config = null;
        this.selectedAccessories = new Map(); // Map<productId, Array<{variant, selectedOptions, quantity}>>
        this.currentAccessory = null;
        this.currentEditIndex = null; // Track which item in array we're editing
        this.mainProductVariant = null;
        this.colorSwatchConfig = this.loadColorSwatchConfig();
        this.categorySubheadings = this.loadCategorySubheadings();
        this.selectionScrollTop = 0;
        this.init();
    }

    async getNextCombinationIdFromCart() {
        // Check cart in real-time to determine the next combination letter
        try {
            const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();

            // If cart is empty, start with A
            if (!cart.items || cart.items.length === 0) {
                return 'A';
            }

            // Cart has items - check for existing combinations
            const existingCombinations = new Set();
            cart.items.forEach(item => {
                if (item.properties && item.properties.Customization) {
                    existingCombinations.add(item.properties.Customization);
                }
            });

            // If no combinations exist in cart, start with A
            if (existingCombinations.size === 0) {
                return 'A';
            }

            // Convert combinations to numbers and sort
            const combinationNumbers = Array.from(existingCombinations)
                .map(combo => this.combinationToNumber(combo))
                .sort((a, b) => a - b);

            // Find the first gap in the sequence
            let nextCounter = 0;
            for (let i = 0; i < combinationNumbers.length; i++) {
                if (combinationNumbers[i] !== i) {
                    // Found a gap! Use this position
                    nextCounter = i;
                    break;
                }
            }

            // If no gaps found, use the next number after the highest
            if (nextCounter === 0 && combinationNumbers.length > 0) {
                const lastNumber = combinationNumbers[combinationNumbers.length - 1];
                if (lastNumber === combinationNumbers.length - 1) {
                    // No gaps, sequence is complete (A, B, C, D...)
                    nextCounter = lastNumber + 1;
                }
            }

            // Convert number back to letter
            return this.numberToCombination(nextCounter);

        } catch (error) {
            console.error('Error checking cart for combinations:', error);
            // Fallback to A if there's an error
            return 'A';
        }
    }

    numberToCombination(counter) {
        // Generate combination ID: A, B, C, ... Z, AA, AB, ... ZZ, AAA, etc.
        let id = '';
        let num = counter;

        do {
            id = String.fromCharCode(65 + (num % 26)) + id;
            num = Math.floor(num / 26) - 1;
        } while (num >= 0);

        return id;
    }

    combinationToNumber(combination) {
        // Convert combination string (A, B, ..., Z, AA, AB) back to number
        let num = 0;
        for (let i = 0; i < combination.length; i++) {
            num = num * 26 + (combination.charCodeAt(i) - 65 + 1);
        }
        return num - 1;
    }

    loadColorSwatchConfig() {
        const config = {};
        const configString = window.themeVariables?.settings?.colorSwatchConfig || '';

        if (configString) {
            const lines = configString.split('\n');
            lines.forEach(line => {
                const parts = line.split(':');
                if (parts.length >= 2) {
                    const name = parts[0].trim().toLowerCase();
                    const value = parts.slice(1).join(':').trim();
                    config[name] = value;
                }
            });
        }

        return config;
    }

    loadCategorySubheadings() {
        const map = {};
        const configString = this.config?.categorySubheadings || window.themeVariables?.settings?.categorySubheadings || '';

        if (!configString) return map;

        const lines = configString.split('\n');
        lines.forEach(line => {
            const trimmed = line.trim();
            if (!trimmed) return;

            const parts = trimmed.split(':');
            if (parts.length < 2) return;

            const key = parts[0].trim().toLowerCase();
            const value = parts.slice(1).join(':').trim();
            if (key && value) {
                map[key] = value;
            }
        });

        return map;
    }

    getProductType(product) {
        // Check for WK_type: tag first
        if (product.tags && Array.isArray(product.tags)) {
            const typeTag = product.tags.find(tag =>
                typeof tag === 'string' && tag.startsWith('WK_type:')
            );
            if (typeTag) {
                // Extract value after 'WK_type:'
                const type = typeTag.substring(8).trim();
                if (type) {
                    return type;
                }
            }
        }
        // Fallback to product_type
        return product.product_type || 'Other';
    }

    isBandCategory(type) {
        if (!type) return false;
        const normalized = type.toLowerCase().trim();
        return normalized === 'band' || normalized === 'hat band';
    }

    sortCategoriesByOrder(categories) {
        // Get the custom order from metafield
        const orderString = this.config?.typeDisplayOrder || '';

        if (!orderString || orderString.trim() === '') {
            // No custom order defined - use alphabetical (default behavior)
            return categories.sort();
        }

        // Parse the order string (comma-separated values)
        const customOrder = orderString
            .split(',')
            .map(type => type.trim())
            .filter(type => type.length > 0);

        // Separate categories into ordered and unordered
        const orderedCategories = [];
        const unorderedCategories = [];

        categories.forEach(category => {
            const index = customOrder.findIndex(
                orderItem => orderItem.toLowerCase() === category.toLowerCase()
            );

            if (index >= 0) {
                orderedCategories.push({ category, order: index });
            } else {
                unorderedCategories.push(category);
            }
        });

        // Sort ordered categories by their defined order
        orderedCategories.sort((a, b) => a.order - b.order);

        // Sort unordered categories alphabetically
        unorderedCategories.sort();

        // Combine: ordered first, then unordered
        return [
            ...orderedCategories.map(item => item.category),
            ...unorderedCategories
        ];
    }

    isCrownPlacementProduct(accessory) {
        const productIds = this.config?.crownPlacement?.productIds || [];
        const numericIds = productIds.map(id => parseInt(id)).filter(id => !isNaN(id));
        return numericIds.includes(accessory.id);
    }

    shouldUseLetterDropdown(accessory, optionName) {
        if (!this.isCrownPlacementProduct(accessory)) return false;
        if (!this.config?.crownPlacement?.showLetterAsDropdown) return false;
        const optionNameLower = (optionName || '').toLowerCase();
        return optionNameLower.includes('letter') || optionNameLower.includes('symbol');
    }

    getSelectedOptions(container) {
        const selectedOptions = [];

        container.querySelectorAll('[data-option-index]').forEach((optionContainer) => {
            const optionIndex = parseInt(optionContainer.dataset.optionIndex);
            if (Number.isNaN(optionIndex)) return;

            const select = optionContainer.querySelector('[data-option-select]');
            if (select) {
                const value = select.value;
                if (value) {
                    selectedOptions[optionIndex] = value;
                }
                return;
            }

            const selectedBtn = optionContainer.querySelector('.variant-swatch--selected, .variant-button--selected');
            if (selectedBtn) {
                selectedOptions[optionIndex] = selectedBtn.dataset.optionValue;
            }
        });

        return selectedOptions;
    }

    getPlacementNote(container) {
        const noteInput = container.querySelector('[data-placement-note]');
        if (!noteInput) return '';
        return noteInput.value.trim();
    }

    async init() {
        this.loadConfig();
        this.bindTriggers();
        this.bindModalEvents();
        this.forceCartDrawerOnAddToCart();
    }

    forceCartDrawerOnAddToCart() {
        // HACK: Force cart drawer to open on pages with customizer
        // This fixes the issue where normal add to cart doesn't open the drawer
        document.addEventListener('variant:add', (event) => {
            // Only intervene if this is NOT from our customizer modal
            if (event.detail?.fromCustomizer) {
                return; // Let our customizer handle it
            }

            // Small delay to ensure cart is updated
            setTimeout(() => {
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer && typeof cartDrawer.show === 'function') {
                    cartDrawer.show();
                }
            }, 100);
        });
    }

    bindTriggers() {
        document.querySelectorAll('[data-customizer-trigger]').forEach(trigger => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.openModal();
            });
        });
    }

    loadConfig() {
        const configElement = document.querySelector('[data-customizer-config]');
        if (configElement) {
            try {
                this.config = JSON.parse(configElement.textContent);
                this.modal = document.querySelector('[data-modal]');
                this.categorySubheadings = this.loadCategorySubheadings();

                if (!this.modal) {
                    console.error('Customizer modal not found');
                    return;
                }

                // Set initial main product variant
                const selectedVariantId = this.getSelectedVariantFromPage();
                if (selectedVariantId) {
                    this.mainProductVariant = this.config.productVariants.find(v => v.id === selectedVariantId);
                }
                if (!this.mainProductVariant && this.config.productVariants.length > 0) {
                    this.mainProductVariant = this.config.productVariants[0];
                }
            } catch (error) {
                console.error('Customizer config error:', error.message);
            }
        } else {
            console.error('Customizer config not found');
        }
    }

    getSelectedVariantFromPage() {
        // Try multiple sources to get currently selected variant

        // 1. Try radio button (most themes)
        const selectedRadio = document.querySelector('input[name="id"]:checked');
        if (selectedRadio && selectedRadio.value) {
            return parseInt(selectedRadio.value);
        }

        // 2. Try hidden input
        const variantInput = document.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
            return parseInt(variantInput.value);
        }

        // 3. Try from product-info component
        const productInfo = document.querySelector('product-info');
        if (productInfo && productInfo.currentVariant) {
            return productInfo.currentVariant.id;
        }

        // 4. Try from URL parameter
        const urlParams = new URLSearchParams(window.location.search);
        const variantParam = urlParams.get('variant');
        if (variantParam) {
            return parseInt(variantParam);
        }

        return null;
    }

    bindModalEvents() {
        if (!this.modal) return;

        // Close modal
        this.modal.querySelectorAll('[data-modal-close]').forEach(btn => {
            btn.addEventListener('click', () => this.closeModal());
        });

        // Close on overlay click
        const overlay = this.modal.querySelector('[data-modal-overlay]');
        if (overlay) {
            overlay.addEventListener('click', () => this.closeModal());
        }

        // Accessory card clicks
        this.modal.querySelectorAll('[data-accessory-card]').forEach(card => {
            card.addEventListener('click', (e) => {
                // Check if the card is out of stock
                const isAvailable = card.dataset.available === 'true';
                if (!isAvailable) {
                    // Don't allow clicking on out-of-stock items
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                const productId = parseInt(card.dataset.productId);
                this.openAccessoryVariantSelector(productId);
            });
        });

        // Review button
        const reviewBtn = this.modal.querySelector('[data-review-button]');
        if (reviewBtn) {
            reviewBtn.addEventListener('click', () => this.showReviewStep());
        }

        // Back buttons
        this.modal.querySelectorAll('[data-back-to-selection]').forEach(btn => {
            btn.addEventListener('click', () => this.showStep('selection'));
        });

        this.modal.querySelectorAll('[data-back-to-selection-from-review]').forEach(btn => {
            btn.addEventListener('click', () => this.showStep('selection'));
        });

        // Variant selection actions
        const cancelBtn = this.modal.querySelector('[data-cancel-variant]');
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.showStep('selection'));
        }

        const saveBtn = this.modal.querySelector('[data-save-variant]');
        if (saveBtn) {
            saveBtn.addEventListener('click', () => this.saveAccessoryVariant());
        }

        // Add bundle to cart
        const addBundleBtn = this.modal.querySelector('[data-add-bundle-to-cart]');
        if (addBundleBtn) {
            addBundleBtn.addEventListener('click', () => this.addBundleToCart());

            // Confirmation checkbox
            const confirmationCheckbox = this.modal.querySelector('[data-confirmation-checkbox]');
            if (confirmationCheckbox) {
                console.log('Checkbox found, initial disabled state:', addBundleBtn.disabled);
                confirmationCheckbox.addEventListener('change', (e) => {
                    addBundleBtn.disabled = !e.target.checked;
                    console.log('Checkbox changed to:', e.target.checked, 'Button disabled:', addBundleBtn.disabled);
                });
            } else {
                console.log('No confirmation checkbox found');
            }
        }

        // ESC key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !this.modal.hidden) {
                this.closeModal();
            }
        });
    }

    async openModal() {
        if (!this.modal || !this.config) {
            console.error('Customizer not initialized');
            return;
        }

        try {
            // Refresh the selected variant from page before opening
            const selectedVariantId = this.getSelectedVariantFromPage();
            if (selectedVariantId) {
                const variant = this.config.productVariants.find(v => v.id === selectedVariantId);
                if (variant) {
                    this.mainProductVariant = variant;
                }
            }

            // Show modal
            this.modal.hidden = false;
            document.body.classList.add('product-customizer-open');
            this.lowerIframeZIndex();
            this.showStep('selection');
            await this.renderCategorizedAccessories();
            this.updateAccessoryCards();

            // Update the main product image AFTER modal is visible
            requestAnimationFrame(() => {
                this.updateMainProductImage();
            });
        } catch (error) {
            console.error('Error opening modal:', error);
            alert('Error opening customizer. Check console for details.');
        }
    }

    updateMainProductImage() {
        const mainImageContainer = this.modal.querySelector('.product-customizer__main-image');
        const mainImage = mainImageContainer?.querySelector('.product-customizer__image');

        if (!mainImage || !this.mainProductVariant) {
            return;
        }

        // Try to get the image URL from various sources
        let imageUrl = null;

        // 1. Check featured_image.src
        if (this.mainProductVariant.featured_image?.src) {
            imageUrl = this.mainProductVariant.featured_image.src;
        }
        // 2. Check featured_media.preview_image.src
        else if (this.mainProductVariant.featured_media?.preview_image?.src) {
            imageUrl = this.mainProductVariant.featured_media.preview_image.src;
        }

        if (imageUrl) {
            // Ensure the URL has a protocol
            if (imageUrl.startsWith('//')) {
                imageUrl = 'https:' + imageUrl;
            }

            // Set the src attribute
            mainImage.src = imageUrl;
            mainImage.alt = this.mainProductVariant.name || this.mainProductVariant.title || this.config.productTitle;

            // CRITICAL: Remove srcset so browser uses our src
            mainImage.removeAttribute('srcset');
            mainImage.removeAttribute('sizes');
        }
    }

    closeModal() {
        if (!this.modal) return;

        this.modal.hidden = true;
        document.body.classList.remove('product-customizer-open');
        this.restoreIframeZIndex();

        // Force body overflow back to normal
        document.body.style.overflow = '';

        this.selectionScrollTop = 0;

        this.showStep('selection');
    }

    lowerIframeZIndex() {
        document.querySelectorAll('iframe').forEach((frame) => {
            if (!frame.dataset.originalZIndex) {
                frame.dataset.originalZIndex = frame.style.zIndex || '';
            }
            frame.style.zIndex = '1';
        });
    }

    restoreIframeZIndex() {
        document.querySelectorAll('iframe').forEach((frame) => {
            if (frame.dataset.originalZIndex !== undefined) {
                frame.style.zIndex = frame.dataset.originalZIndex;
                delete frame.dataset.originalZIndex;
            } else {
                frame.style.zIndex = '';
            }
        });
    }

    showStep(stepName) {
        const content = this.modal?.querySelector('.product-customizer-modal__content');
        const currentStep = this.modal?.querySelector('[data-step]:not([hidden])');

        if (content && currentStep?.dataset.step === 'selection' && stepName !== 'selection') {
            this.selectionScrollTop = content.scrollTop;
        }

        const steps = this.modal.querySelectorAll('[data-step]');
        steps.forEach(step => {
            step.hidden = step.dataset.step !== stepName;
        });

        if (content && stepName === 'selection') {
            requestAnimationFrame(() => {
                content.scrollTop = this.selectionScrollTop || 0;
            });
        }else if (content) {
            requestAnimationFrame(() => {
                content.scrollTop = 0;
            });
        }
    }

    async renderCategorizedAccessories() {
        const container = this.modal.querySelector('[data-accessories-list]');
        if (!container || !this.config.accessories) return;

        // Check which product types should be visible
        const visibleProductTypes = await this.getVisibleProductTypes();

        // Check which products should be disabled (inverse logic)
        const disabledProductIds = this.getDisabledProductIds();

        // Filter accessories based on showOutOfStock setting
        const showOutOfStock = this.config.showOutOfStock !== false; // Default to true if not set
        const accessoriesToShow = showOutOfStock
            ? this.config.accessories
            : this.config.accessories.filter(accessory => {
                // Only show if at least one variant is available
                return accessory.variants.some(v => v.available);
            });

        // Group accessories by product_type (using tag-based type if available)
        const categorized = {};
        accessoriesToShow.forEach(accessory => {
            const category = this.getProductType(accessory);
            if (!categorized[category]) {
                categorized[category] = [];
            }
            categorized[category].push(accessory);
        });

        // Build HTML
        let html = '';
        const sortedCategories = this.sortCategoriesByOrder(Object.keys(categorized));
        sortedCategories.forEach(category => {
            const shouldHide = visibleProductTypes[category] === false;
            const categoryKey = category.toLowerCase();
            const categorySubheading = this.categorySubheadings[categoryKey];

            html += `
                <div class="product-customizer__category${shouldHide ? ' product-customizer__category--hidden' : ''}" data-category="${category}">
                    <h4 class="product-customizer__category-title">${category}</h4>
                     ${categorySubheading ? `<div class="product-customizer__category-subheading">${categorySubheading}</div>` : ''}
                    <div class="product-customizer__category-grid">
            `;

            categorized[category].forEach(accessory => {
                const isAvailable = accessory.variants.some(v => v.available);
                const isDisabled = disabledProductIds.has(accessory.id);

                html += `
                    <div 
                        class="product-customizer__accessory-card${!isAvailable ? ' accessory-card--out-of-stock' : ''}${isDisabled ? ' accessory-card--disabled' : ''}" 
                        data-accessory-card
                        data-product-id="${accessory.id}"
                        data-available="${isAvailable}"
                        data-disabled="${isDisabled}"
                    >
                        <div class="product-customizer__accessory-image">
                            ${accessory.featuredImage ?
                        `<img src="${accessory.featuredImage}" alt="${accessory.title}" class="accessory-image" loading="lazy" />` :
                        '<div class="accessory-image-placeholder"></div>'
                    }
                            ${!isAvailable ? '<div class="product-customizer__out-of-stock-badge"><span>Out of Stock</span></div>' : ''}
                            ${isDisabled ? '<div class="product-customizer__disabled-badge"><span>Not Available</span></div>' : ''}
                        </div>
                        <div class="product-customizer__accessory-info">
                            <h4 class="product-customizer__accessory-title">${accessory.title}</h4>
                            <p class="product-customizer__accessory-price">$${(accessory.price / 100).toFixed(2)}</p>
                        </div>
                        <div class="product-customizer__accessory-overlay" data-accessory-overlay>
                            <span class="product-customizer__accessory-status" data-accessory-status></span>
                        </div>
                    </div>
                `;
            });

            html += `
                    </div>
                </div>
            `;
        });

        container.innerHTML = html;

        // Re-bind click events
        container.querySelectorAll('[data-accessory-card]').forEach(card => {
            card.addEventListener('click', (e) => {
                const isAvailable = card.dataset.available === 'true';
                const isDisabled = card.dataset.disabled === 'true';

                if (!isAvailable || isDisabled) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                const productId = parseInt(card.dataset.productId);
                this.openAccessoryVariantSelector(productId);
            });
        });
    }

    async getVisibleProductTypes() {
        const visibilityMap = {};

        // Get ONLY modal selected accessories (NOT cart items)
        let modalSelectedProducts = [];
        this.selectedAccessories.forEach((selections, productId) => {
            selections.forEach(selection => {
                modalSelectedProducts.push({
                    id: productId,
                    type: this.getProductType(selection.product).toLowerCase()
                });
            });
        });

        // Process configured exclusions from config
        if (this.config.productTypeExclusions && this.config.productTypeExclusions.length > 0) {
            this.config.productTypeExclusions.forEach((exclusion) => {
                if (!exclusion || !exclusion.productType || !exclusion.triggerProductIds) {
                    return;
                }

                const { productType, triggerProductIds } = exclusion;

                // Convert all IDs to numbers for comparison
                const numericTriggerIds = triggerProductIds.map(id => parseInt(id)).filter(id => !isNaN(id));
                const numericSelectedIds = modalSelectedProducts.map(p => parseInt(p.id));

                // Check if any trigger (allowed) product is selected
                const hasTriggerProduct = numericTriggerIds.some(triggerId =>
                    numericSelectedIds.includes(triggerId)
                );

                // Check if any non-trigger band is selected
                const hasNonTriggerBand = modalSelectedProducts.some(product => {
                    const isBand = product.type.includes('band') || product.type.includes('hat band');
                    const isTriggerProduct = numericTriggerIds.includes(product.id);
                    return isBand && !isTriggerProduct;
                });

                // Always keep the excluded category visible if any of its products are selected
                const hasExcludedTypeSelected = modalSelectedProducts.some(product => {
                    return product.type.toLowerCase() === productType.toLowerCase();
                });

                // Logic: Show product type if:
                // 1. A trigger product is selected (priority - overrides everything), OR
                 // 2. No non-trigger bands are selected, OR
                // 3. A product from this category is already selected
                const shouldShow = hasTriggerProduct || !hasNonTriggerBand || hasExcludedTypeSelected;

                visibilityMap[productType] = shouldShow;
            });
        }

        return visibilityMap;
    }

    getDisabledProductIds() {
        const disabledIds = new Set();

        try {
            if (!this.config || !this.config.productTypeExclusions || this.config.productTypeExclusions.length === 0) {
                return disabledIds;
            }

            if (!this.config.accessories) {
                return disabledIds;
            }

            // Get modal selected products
            let modalSelectedProducts = [];
            this.selectedAccessories.forEach((selections, productId) => {
                selections.forEach(selection => {
                    modalSelectedProducts.push({
                        id: productId,
                        type: this.getProductType(selection.product).toLowerCase()
                    });
                });
            });

            // For each exclusion rule, check if the excluded product type is selected
            this.config.productTypeExclusions.forEach((exclusion) => {
                if (!exclusion || !exclusion.productType || !exclusion.triggerProductIds) {
                    return;
                }

                const { productType, triggerProductIds } = exclusion;

                // Check if any product of the excluded type is selected
                const hasExcludedTypeSelected = modalSelectedProducts.some(product => {
                    return product.type.toLowerCase() === productType.toLowerCase();
                });

                if (hasExcludedTypeSelected) {
                    // If excluded type is selected, disable all band products that are NOT trigger products
                    const numericTriggerIds = triggerProductIds.map(id => parseInt(id)).filter(id => !isNaN(id));

                    // Find all band products
                    this.config.accessories.forEach(accessory => {
                        const productType = this.getProductType(accessory).toLowerCase();
                        const isBand = productType.includes('bands');
                        const isTriggerProduct = numericTriggerIds.includes(accessory.id);

                        // Disable bands that are NOT in the trigger list
                        if (isBand && !isTriggerProduct) {
                            disabledIds.add(accessory.id);
                        }
                    });
                }
            });
        } catch (error) {
            console.error('Error in getDisabledProductIds:', error);
        }

        return disabledIds;
    }

    openAccessoryVariantSelector(productId, editIndex = null) {
        const accessory = this.config.accessories.find(a => a.id === productId);
        if (!accessory) return;

        this.currentAccessory = accessory;
        this.currentEditIndex = editIndex;
        this.hideVariantError();
        this.renderVariantSelector(accessory, editIndex);
        this.showStep('variant-selection');
    }

    renderVariantSelector(accessory, editIndex = null) {
        const container = this.modal.querySelector('[data-variant-content]');
        if (!container) return;

        // Check if editing an existing selection
        let existingSelection = null;
        if (editIndex !== null) {
            const selections = this.selectedAccessories.get(accessory.id);
            if (selections && selections[editIndex]) {
                existingSelection = selections[editIndex];
            }
        }

        let html = `
      <div class="variant-selector">
        <div class="variant-selector__image">
          ${accessory.featuredImage ?
                `<img src="${accessory.featuredImage}" alt="${accessory.title}" class="variant-selector__img" />` :
                '<div class="variant-selector__placeholder"></div>'
            }
        </div>
        <div class="variant-selector__info">
                    <h3 class="variant-selector__title">${accessory.title}, $${(accessory.price / 100).toFixed(2)}</h3>
    `;

        // Render variant options
        if (accessory.options && accessory.options.length > 0 && accessory.options[0] !== 'Title') {
            // accessory.options is an array of option names like ["Color", "Size"]
            // accessory.variants[].options is an array of values like ["Gold", "Small"]

            accessory.options.forEach((optionName, optionIndex) => {
                // Safety check for option name
                if (!optionName || optionName === 'Title') return;

                const values = [...new Set(accessory.variants.map(v => v.options[optionIndex]).filter(v => v != null))];
                const selectedValue = existingSelection && existingSelection.selectedOptions[optionIndex]
                    ? existingSelection.selectedOptions[optionIndex]
                    : '';

                if (this.shouldUseLetterDropdown(accessory, optionName)) {
                    html += `
                    <div class="variant-selector__option">
                        <label class="variant-selector__option-label">${optionName} — ${selectedValue}</label>
                        <div class="variant-selector__dropdown" data-option-index="${optionIndex}">
                            <select class="variant-selector__select" data-option-select>
                                <option value="">Select ${optionName}</option>
                    `;

                    values.forEach(value => {
                        if (!value) return;

                        const hasAvailableVariant = accessory.variants.some(variant => {
                            return variant.options[optionIndex] === value && variant.available;
                        });

                        html += `
                                <option value="${value}" ${selectedValue === value ? 'selected' : ''} ${!hasAvailableVariant ? 'disabled' : ''}>${value}</option>
                        `;
                    });

                    html += `
                            </select>
                        </div>
                    </div>
                    `;

                    return;
                }

                html += `
                    <div class="variant-selector__option">
                        <label class="variant-selector__option-label">${optionName} — ${selectedValue}</label>
                        <div class="variant-selector__swatches" data-option-index="${optionIndex}">
                `;

                values.forEach(value => {
                    if (!value) return;

                    const optionNameLower = optionName.toLowerCase();
                    const isColor = optionNameLower.includes('color') || optionNameLower.includes('colour');

                    // Check if this option value has any available variants
                    const hasAvailableVariant = accessory.variants.some(variant => {
                        return variant.options[optionIndex] === value && variant.available;
                    });

                    if (isColor) {
                        const swatchStyle = this.getSwatchStyle(value);
                        html += `
                            <button 
                                type="button" 
                                class="variant-swatch${!hasAvailableVariant ? ' variant-swatch--out-of-stock' : ''}" 
                                data-option-value="${value}"
                                data-option-index="${optionIndex}"
                                data-available="${hasAvailableVariant}"
                                title="${value}${!hasAvailableVariant ? ' (Out of Stock)' : ''}"
                                style="${swatchStyle}"
                                ${!hasAvailableVariant ? 'disabled' : ''}
                            >
                                <span class="visually-hidden">${value}${!hasAvailableVariant ? ' (Out of Stock)' : ''}</span>
                            </button>
                        `;
                    } else {
                        html += `
                            <button 
                                type="button" 
                                class="variant-button${!hasAvailableVariant ? ' variant-button--out-of-stock' : ''}" 
                                data-option-value="${value}"
                                data-option-index="${optionIndex}"
                                data-available="${hasAvailableVariant}"
                                ${!hasAvailableVariant ? 'disabled' : ''}
                            >
                                ${value}
                            </button>
                        `;
                    }
                });

                html += `
                        </div>
                    </div>
                `;
            });
        } else {
            // Product with no variants or only default variant
            html += '<p class="text-subdued">This product has a single variant and will be added as-is.</p>';
        }

        if (this.isCrownPlacementProduct(accessory)) {
            const existingNote = existingSelection && existingSelection.placementNote ? existingSelection.placementNote : '';
            html += `
            <div class="variant-selector__note">
                <label class="variant-selector__option-label" for="placement-note-${accessory.id}">Branding Instructions</label>
                <textarea id="placement-note-${accessory.id}" class="variant-selector__textarea" data-placement-note rows="3" placeholder="Add any specific instructions for your branding here (placement details, spacing preferences, or special requests)">${existingNote}</textarea>
            </div>
            `;
        }

        html += `
        </div>
      </div>
    `;

        container.innerHTML = html;

        // If there's an existing selection, apply it ONCE per option
        if (existingSelection && existingSelection.selectedOptions) {
            existingSelection.selectedOptions.forEach((savedValue, optIndex) => {
                if (savedValue) {
                    // Find this specific option group
                    const optionGroup = container.querySelector(`[data-option-index="${optIndex}"]`);
                    if (optionGroup) {
                        // First remove selected from ALL buttons in this group
                        optionGroup.querySelectorAll('[data-option-value]').forEach(btn => {
                            btn.classList.remove('variant-swatch--selected', 'variant-button--selected');
                        });

                        // Then add selected to ONLY the matching button
                        const matchingBtn = optionGroup.querySelector(`[data-option-value="${savedValue}"]`);
                        if (matchingBtn) {
                            matchingBtn.classList.add(
                                matchingBtn.classList.contains('variant-swatch')
                                    ? 'variant-swatch--selected'
                                    : 'variant-button--selected'
                            );
                        }
                    }
                }
            });
        }

        // Bind variant option clicks
        container.querySelectorAll('[data-option-value]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Prevent clicking on out-of-stock options
                if (btn.dataset.available === 'false' || btn.disabled) {
                    return;
                }

                // Hide error message as soon as user selects any option
                this.hideVariantError();

                const optionIndex = parseInt(btn.dataset.optionIndex);
                const optionValue = btn.dataset.optionValue;

                // Remove selected from ALL buttons in this option group
                const swatchContainer = btn.closest('.variant-selector__swatches');
                swatchContainer.querySelectorAll('[data-option-value]').forEach(b => {
                    b.classList.remove('variant-swatch--selected', 'variant-button--selected');
                });

                // Add selected to clicked button
                btn.classList.add(btn.classList.contains('variant-swatch') ? 'variant-swatch--selected' : 'variant-button--selected');

                // Update label text
                const optionContainer = btn.closest('.variant-selector__option');
                if (optionContainer) {
                    const label = optionContainer.querySelector('.variant-selector__option-label');
                    if (label && accessory.options) {
                        const optionName = accessory.options[optionIndex];
                        label.textContent = `${optionName} — ${optionValue}`;
                    }
                }

                // Update availability of other options based on current selection
                this.updateVariantAvailability(container, accessory);

                // Update variant image based on current selection
                this.updateVariantImage(container, accessory);
            });
        });

        // Bind dropdown changes
        container.querySelectorAll('[data-option-select]').forEach(select => {
            select.addEventListener('change', () => {
                this.hideVariantError();

                const optionContainer = select.closest('[data-option-index]');
                const optionIndex = optionContainer ? parseInt(optionContainer.dataset.optionIndex) : null;
                if (optionContainer && optionIndex !== null && accessory.options) {
                    const optionName = accessory.options[optionIndex];
                    const label = optionContainer.closest('.variant-selector__option')?.querySelector('.variant-selector__option-label');
                    if (label) {
                        label.textContent = `${optionName} — ${select.value || ''}`;
                    }
                }

                this.updateVariantAvailability(container, accessory);
                this.updateVariantImage(container, accessory);
            });
        });

        // Initial availability check on load
        this.updateVariantAvailability(container, accessory);
    }

    updateVariantImage(container, accessory) {
        // Get all currently selected options
        const selectedOptions = this.getSelectedOptions(container);

        // Find matching variant
        let matchedVariant = null;

        // Only look for exact match if all options are selected
        const allOptionsSelected = selectedOptions.length === accessory.options.length &&
            selectedOptions.every(opt => opt !== undefined);

        if (allOptionsSelected) {
            matchedVariant = accessory.variants.find(variant => {
                return variant.options.every((opt, idx) => opt === selectedOptions[idx]);
            });
        } else {
            // Partial match - find first variant that matches selected options
            matchedVariant = accessory.variants.find(variant => {
                return selectedOptions.every((selectedValue, idx) => {
                    if (!selectedValue) return true; // Skip unselected options
                    return variant.options[idx] === selectedValue;
                });
            });
        }

        // Update image if we found a matching variant
        if (matchedVariant && matchedVariant.featured_image) {
            const variantImage = container.querySelector('.variant-selector__img');
            if (variantImage) {
                let imageUrl = matchedVariant.featured_image.src;

                // Ensure the URL has a protocol
                if (imageUrl.startsWith('//')) {
                    imageUrl = 'https:' + imageUrl;
                }

                variantImage.src = imageUrl;
                variantImage.alt = matchedVariant.name || accessory.title;
            }
        }
    }

    updateVariantAvailability(container, accessory) {
        // Get all currently selected options
        const selectedOptions = this.getSelectedOptions(container);

        // Update each option group
        container.querySelectorAll('[data-option-index]').forEach((optionContainer) => {
            const optionIndex = parseInt(optionContainer.dataset.optionIndex);
            const select = optionContainer.querySelector('[data-option-select]');
            if (select) {
                const options = Array.from(select.options).filter(option => option.value);

                options.forEach(option => {
                    const optionValue = option.value;

                    // Build test options array with this value
                    const testOptions = [...selectedOptions];
                    testOptions[optionIndex] = optionValue;

                    // Check if any variant matches this combination and is available
                    const hasAvailableVariant = accessory.variants.some(variant => {
                        const matches = testOptions.every((selectedValue, idx) => {
                            if (!selectedValue) return true;
                            return variant.options[idx] === selectedValue;
                        });

                        return matches && variant.available;
                    });

                    option.disabled = !hasAvailableVariant;
                });

                return;
            }

            const buttons = optionContainer.querySelectorAll('[data-option-value]');

            buttons.forEach(btn => {
                const optionValue = btn.dataset.optionValue;

                // Build test options array with this value
                const testOptions = [...selectedOptions];
                testOptions[optionIndex] = optionValue;

                // Check if any variant matches this combination and is available
                const hasAvailableVariant = accessory.variants.some(variant => {
                    // Check if this variant matches all selected options
                    const matches = testOptions.every((selectedValue, idx) => {
                        // If no value selected for this index, it matches
                        if (!selectedValue) return true;
                        // Otherwise, check if variant option matches
                        return variant.options[idx] === selectedValue;
                    });

                    return matches && variant.available;
                });

                // Update button state
                const isSwatch = btn.classList.contains('variant-swatch');
                const isButton = btn.classList.contains('variant-button');

                if (hasAvailableVariant) {
                    // Available - remove out-of-stock class
                    btn.classList.remove('variant-swatch--out-of-stock', 'variant-button--out-of-stock');
                    btn.disabled = false;
                    btn.dataset.available = 'true';

                    // Update title
                    const originalTitle = optionValue;
                    btn.setAttribute('title', originalTitle);
                    if (btn.querySelector('.visually-hidden')) {
                        btn.querySelector('.visually-hidden').textContent = originalTitle;
                    }
                } else {
                    // Out of stock - add appropriate class
                    if (isSwatch) {
                        btn.classList.add('variant-swatch--out-of-stock');
                    } else if (isButton) {
                        btn.classList.add('variant-button--out-of-stock');
                    }
                    btn.disabled = true;
                    btn.dataset.available = 'false';

                    // Update title
                    const originalTitle = optionValue;
                    btn.setAttribute('title', `${originalTitle} (Out of Stock)`);
                    if (btn.querySelector('.visually-hidden')) {
                        btn.querySelector('.visually-hidden').textContent = `${originalTitle} (Out of Stock)`;
                    }
                }
            });
        });
    }

    getSwatchStyle(colorName) {
        const valueLower = colorName.toLowerCase().trim();

        // Check theme's color config first
        if (this.colorSwatchConfig[valueLower]) {
            const configValue = this.colorSwatchConfig[valueLower];

            // If it's an image file
            if (configValue.includes('.png') || configValue.includes('.jpg') || configValue.includes('.jpeg')) {
                const imageUrl = `https://cdn.shopify.com/s/files/1/0258/2948/3380/files/${configValue}`;
                return `--swatch-background: url(${imageUrl})`;
            }
            // If it's a hex color or gradient
            else if (configValue.startsWith('#')) {
                return `--swatch-background: linear-gradient(to right, ${configValue}, ${configValue})`;
            }
            else if (configValue.includes('linear-gradient')) {
                return `--swatch-background: ${configValue}`;
            }
        }

        // Fallback to basic color mapping
        const colorValue = this.getColorValue(colorName);
        return `--swatch-background: linear-gradient(to right, ${colorValue}, ${colorValue});`;
    }

    getColorValue(colorName) {
        const colorMap = {
            'black': '#000000',
            'white': '#ffffff',
            'stone': '#d4c5b0',
            'brown': '#8b4513',
            'tan': '#d2b48c',
            'grey': '#808080',
            'gray': '#808080',
            'charcoal': '#36454f',
            'burgundy': '#800020',
            'wine': '#722f37',
            'navy': '#000080',
            'blue': '#0000ff',
            'red': '#ff0000',
            'green': '#008000',
            'pink': '#ffc0cb',
            'rose': '#ff007f',
            'purple': '#800080',
            'yellow': '#ffff00',
            'orange': '#ffa500',
            'beige': '#f5f5dc',
            'cream': '#fffdd0',
            'ivory': '#fffff0',
            'camel': '#c19a6b',
            'cognac': '#9a463d'
        };

        const lowerColor = colorName.toLowerCase();
        for (const [key, value] of Object.entries(colorMap)) {
            if (lowerColor.includes(key)) {
                return value;
            }
        }

        return '#cccccc';
    }

    showVariantError(message) {
        const errorContainer = this.modal.querySelector('[data-variant-error]');
        if (!errorContainer) return;

        const errorMessage = errorContainer.querySelector('[data-error-message]');
        errorMessage.textContent = message;
        errorContainer.hidden = false;

        // Scroll error into view
        errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    hideVariantError() {
        const errorContainer = this.modal.querySelector('[data-variant-error]');
        if (!errorContainer) return;
        errorContainer.hidden = true;
    }

    async saveAccessoryVariant() {
        if (!this.currentAccessory) return;

        const container = this.modal.querySelector('[data-variant-content]');
        const selectedOptions = this.getSelectedOptions(container);
        const placementNote = this.getPlacementNote(container);

        // Check if this is a product with no variants (or only default Title variant)
        const hasOptions = this.currentAccessory.options &&
            this.currentAccessory.options.length > 0 &&
            this.currentAccessory.options[0] !== 'Title';

        if (!hasOptions) {
            // Single variant product - just select the first (and only) variant
            const selectedVariant = this.currentAccessory.variants[0];

            const newSelection = {
                product: this.currentAccessory,
                variant: selectedVariant,
                selectedOptions: [],
                quantity: 1,
                placementNote: placementNote
            };

            if (this.currentEditIndex !== null) {
                // Editing existing item
                const selections = this.selectedAccessories.get(this.currentAccessory.id) || [];
                selections[this.currentEditIndex] = newSelection;
                this.selectedAccessories.set(this.currentAccessory.id, selections);
            } else {
                // Adding new item
                const selections = this.selectedAccessories.get(this.currentAccessory.id) || [];
                selections.push(newSelection);
                this.selectedAccessories.set(this.currentAccessory.id, selections);
            }

            // Re-render categories in case this selection unlocks new product types
            await this.renderCategorizedAccessories();
            this.updateAccessoryCards();
            this.showStep('selection');
            this.currentAccessory = null;
            this.currentEditIndex = null;
            return;
        }

        // Find matching variant
        let selectedVariant = null;
        if (this.currentAccessory.variants.length === 1) {
            selectedVariant = this.currentAccessory.variants[0];
        } else {
            const missingOptions = [];
            if (this.currentAccessory.options && this.currentAccessory.options.length > 0) {
                this.currentAccessory.options.forEach((optionName, optionIndex) => {
                    if (!optionName || optionName === 'Title') return;
                    if (!selectedOptions[optionIndex]) {
                        missingOptions.push(optionName);
                    }
                });
            }

            if (missingOptions.length > 0) {
                if (missingOptions.length === 1) {
                    this.showVariantError(`Please select ${missingOptions[0]}`);
                } else {
                    this.showVariantError(`Please select: ${missingOptions.join(', ')}`);
                }
                return;
            }

            selectedVariant = this.currentAccessory.variants.find(variant => {
                return variant.options.every((opt, idx) => opt === selectedOptions[idx]);
            });
        }

        if (!selectedVariant) {
            this.showVariantError('Please select a valid option combination');
            return;
        }

        // Check if selected variant is available
        if (!selectedVariant.available) {
            this.showVariantError('This variant is out of stock. Please select a different option.');
            return;
        }

        // Save selection
        const newSelection = {
            product: this.currentAccessory,
            variant: selectedVariant,
            selectedOptions: selectedOptions,
            quantity: 1,
            placementNote: placementNote
        };

        if (this.currentEditIndex !== null) {
            // Editing existing item
            const selections = this.selectedAccessories.get(this.currentAccessory.id) || [];
            selections[this.currentEditIndex] = newSelection;
            this.selectedAccessories.set(this.currentAccessory.id, selections);
        } else {
            // Adding new item - check if this exact variant already exists
            const selections = this.selectedAccessories.get(this.currentAccessory.id) || [];
            const existingIndex = selections.findIndex(sel => sel.variant.id === selectedVariant.id);

            if (existingIndex !== -1) {
                // Same variant exists, increment quantity
                selections[existingIndex].quantity += 1;
            } else {
                // New variant, add to array
                selections.push(newSelection);
            }
            this.selectedAccessories.set(this.currentAccessory.id, selections);
        }

        // Re-render categories in case this selection unlocks new product types
        await this.renderCategorizedAccessories();

        // Update UI and go back
        this.updateAccessoryCards();
        this.showStep('selection');
        this.currentAccessory = null;
        this.currentEditIndex = null;
    }

    updateAccessoryCards() {
        this.modal.querySelectorAll('[data-accessory-card]').forEach(card => {
            const productId = parseInt(card.dataset.productId);
            const overlay = card.querySelector('[data-accessory-overlay]');
            const status = card.querySelector('[data-accessory-status]');

            if (this.selectedAccessories.has(productId)) {
                card.classList.add('product-customizer__accessory-card--selected');
                const selections = this.selectedAccessories.get(productId);
                const totalQuantity = selections.reduce((sum, sel) => sum + sel.quantity, 0);

                if (overlay && status) {
                    overlay.classList.add('product-customizer__accessory-overlay--active');

                    let html = `<div class="accessory-status__content">`;

                    // Show count badge
                    html += `<div class="accessory-status__badge">${totalQuantity} selected</div>`;

                    // List each variant
                    html += `<div class="accessory-status__items">`;
                    selections.forEach((sel, index) => {
                        const variantText = sel.selectedOptions.length > 0 ? sel.selectedOptions.join(', ') : 'Default';
                        html += `
                            <div class="accessory-status__item">
                                <span class="accessory-status__variant">${variantText} (×${sel.quantity})</span>
                                <div class="accessory-status__item-actions">
                                    <button type="button" class="btn-icon" data-edit-item="${productId}-${index}" title="Edit">
                                        <span>✎</span>
                                    </button>
                                    <button type="button" class="btn-icon" data-remove-item="${productId}-${index}" title="Remove">
                                        <span>×</span>
                                    </button>
                                </div>
                            </div>
                        `;
                    });
                    html += `</div>`;

                    // Add Another button
                    html += `
                        <button type="button" class="btn-text accessory-status__add-another" data-add-another="${productId}">
                            + Add Another
                        </button>
                    `;

                    html += `</div>`;
                    status.innerHTML = html;

                    // Bind actions
                    selections.forEach((sel, index) => {
                        const editBtn = status.querySelector(`[data-edit-item="${productId}-${index}"]`);
                        const removeBtn = status.querySelector(`[data-remove-item="${productId}-${index}"]`);

                        if (editBtn) {
                            editBtn.addEventListener('click', (e) => {
                                e.stopPropagation();
                                this.openAccessoryVariantSelector(productId, index);
                            });
                        }

                        if (removeBtn) {
                            removeBtn.addEventListener('click', (e) => {
                                e.stopPropagation();
                                this.removeAccessoryItem(productId, index);
                            });
                        }
                    });

                    // Bind "Add Another" button
                    const addAnotherBtn = status.querySelector(`[data-add-another="${productId}"]`);
                    if (addAnotherBtn) {
                        addAnotherBtn.addEventListener('click', (e) => {
                            e.stopPropagation();
                            this.openAccessoryVariantSelector(productId, null);
                        });
                    }
                }
            } else {
                card.classList.remove('product-customizer__accessory-card--selected');
                if (overlay) {
                    overlay.classList.remove('product-customizer__accessory-overlay--active');
                }
                if (status) {
                    status.innerHTML = '';
                }
            }
        });
    }

    async removeAccessoryItem(productId, index) {
        const selections = this.selectedAccessories.get(productId);
        if (!selections) return;

        selections.splice(index, 1);

        if (selections.length === 0) {
            this.selectedAccessories.delete(productId);
        } else {
            this.selectedAccessories.set(productId, selections);
        }

        // Re-render categories in case removing this product hides some product types
        await this.renderCategorizedAccessories();
        this.updateAccessoryCards();
    }

    showReviewStep() {
        this.renderReview();
        this.showStep('review');
    }

    renderReview() {
        const reviewContainer = this.modal.querySelector('[data-review-items]');
        const accessoriesReview = this.modal.querySelector('[data-accessories-review]');

        if (!reviewContainer || !accessoriesReview) return;

        // Update main product info
        const mainProductVariant = this.modal.querySelector('[data-main-product-variant]');
        if (mainProductVariant && this.mainProductVariant) {
            mainProductVariant.textContent = this.mainProductVariant.title;
        }

        // Update main product image in review step
        const mainReviewItem = this.modal.querySelector('[data-main-product-review]');
        if (mainReviewItem && this.mainProductVariant) {
            const reviewImage = mainReviewItem.querySelector('.review-item__img');
            if (reviewImage) {
                let imageUrl = null;

                if (this.mainProductVariant.featured_image?.src) {
                    imageUrl = this.mainProductVariant.featured_image.src;
                } else if (this.mainProductVariant.featured_media?.preview_image?.src) {
                    imageUrl = this.mainProductVariant.featured_media.preview_image.src;
                }

                if (imageUrl) {
                    if (imageUrl.startsWith('//')) {
                        imageUrl = 'https:' + imageUrl;
                    }
                    reviewImage.src = imageUrl;
                    reviewImage.alt = this.mainProductVariant.title || this.config.productTitle;
                    reviewImage.removeAttribute('srcset');
                    reviewImage.removeAttribute('sizes');
                }
            }
        }

        // Render accessories
        let accessoriesHtml = '';
        this.selectedAccessories.forEach((selections, productId) => {
            // selections is now an array
            selections.forEach((selection) => {
                // Get the variant-specific image or fallback to product image
                let imageUrl = null;

                if (selection.variant.featured_image?.src) {
                    imageUrl = selection.variant.featured_image.src;
                } else if (selection.product.featuredImage) {
                    imageUrl = selection.product.featuredImage;
                }

                // Ensure URL has protocol
                if (imageUrl && imageUrl.startsWith('//')) {
                    imageUrl = 'https:' + imageUrl;
                }

                accessoriesHtml += `
            <div class="product-customizer-review__item">
              <div class="review-item__image">
                ${imageUrl ?
                        `<img src="${imageUrl}" alt="${selection.product.title}" class="review-item__img" />` :
                        '<div class="review-item__placeholder"></div>'
                    }
              </div>
              <div class="review-item__details">
                <h4 class="review-item__title">${selection.product.title}</h4>
                <p class="review-item__price">$${(selection.variant.price / 100).toFixed(2)} × ${selection.quantity}</p>
                ${selection.selectedOptions.length > 0 ?
                        `<p class="review-item__variant">${selection.selectedOptions.join(', ')}</p>` :
                        ''
                    }
                                ${selection.placementNote ?
                                                `<p class="review-item__note">Branding Instructions: ${selection.placementNote}</p>` :
                                                ''
                                        }
              </div>
            </div>
          `;
            });
        });

        accessoriesReview.innerHTML = accessoriesHtml;

        // Calculate total
        this.updateTotal();
    }

    updateTotal() {
        const totalElement = this.modal.querySelector('[data-total-price]');
        const finalAddPrice = this.modal.querySelector('[data-final-add-price]');

        let total = this.mainProductVariant ? this.mainProductVariant.price : this.config.productPrice;

        this.selectedAccessories.forEach((selections) => {
            selections.forEach((selection) => {
                total += selection.variant.price * selection.quantity;
            });
        });

        const formattedTotal = `$${(total / 100).toFixed(2)}`;

        if (totalElement) {
            totalElement.textContent = formattedTotal;
        }

        if (finalAddPrice) {
            finalAddPrice.textContent = ` - ${formattedTotal}`;
        }
    }

    async addBundleToCart() {
        const addBtn = this.modal.querySelector('[data-add-bundle-to-cart]');
        if (!addBtn) return;

        // Disable button
        addBtn.disabled = true;
        addBtn.setAttribute('aria-busy', 'true');
        const originalText = addBtn.innerHTML;
        addBtn.innerHTML = '<span>Adding...</span>';

        try {
            const items = [];

            // Generate a unique combination ID based on current cart state
            const combinationId = await this.getNextCombinationIdFromCart();

            // Add main product
            if (this.mainProductVariant) {
                items.push({
                    id: this.mainProductVariant.id,
                    quantity: 1,
                    properties: {
                        'Customization': combinationId,
                        '_customization_role': 'main'
                    }
                });
            }

            // Add accessories
            this.selectedAccessories.forEach((selections) => {
                selections.forEach((selection) => {
                    const accessoryProperties = {
                        'Customization': combinationId,
                        '_customization_role': 'accessory'
                    };

                    if (selection.placementNote) {
                        accessoryProperties['Branding Instructions'] = selection.placementNote;
                    }

                    items.push({
                        id: selection.variant.id,
                        quantity: selection.quantity,
                        properties: accessoryProperties
                    });
                });
            });

            // Use the EXACT same method as the theme's product form
            let sectionsToBundle = ['variant-added'];

            // Dispatch the prepare event that the theme uses (on document, not documentElement)
            document.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
                bubbles: true,
                detail: { sections: sectionsToBundle }
            }));

            // Create FormData with proper format for multiple items
            const formData = new FormData();

            // Add each item with proper array notation, including properties
            items.forEach((item, index) => {
                formData.append(`items[${index}][id]`, item.id);
                formData.append(`items[${index}][quantity]`, item.quantity);

                // Add line item properties
                if (item.properties) {
                    Object.entries(item.properties).forEach(([key, value]) => {
                        formData.append(`items[${index}][properties][${key}]`, value);
                    });
                }
            });

            formData.set('sections', sectionsToBundle.join(','));
            formData.set('sections_url', `${Shopify.routes.root}variants/${this.mainProductVariant.id}`);

            // Use the EXACT same fetch as the theme
            const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
                body: formData,
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            const responseJson = await response.json();

            if (response.ok) {
                // Check if cart type is page (redirect to cart page)
                if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
                    return window.location.href = `${Shopify.routes.root}cart`;
                }

                // Get cart content exactly as theme does
                const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
                cartContent['sections'] = responseJson['sections'];

                // Dispatch cart:change event first to update cart drawer content
                document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                    bubbles: true,
                    detail: {
                        baseEvent: 'variant:add',
                        cart: cartContent,
                        fromCustomizer: true  // Mark this as from customizer
                    }
                }));

                // Close modal to remove overflow:hidden from body
                this.closeModal();
                this.selectedAccessories.clear();

                // Now manually open the cart drawer
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer && typeof cartDrawer.show === 'function') {
                    // Give a tiny delay to ensure modal is fully closed
                    requestAnimationFrame(() => {
                        cartDrawer.show();
                    });
                }

            } else {
                // Handle error exactly as theme does
                document.dispatchEvent(new CustomEvent('cart:error', {
                    bubbles: true,
                    detail: {
                        error: responseJson['description']
                    }
                }));

                alert(responseJson['description'] || 'Failed to add items to cart. Please try again.');
            }

        } catch (error) {
            console.error('Add to cart error:', error.message);
            alert('Failed to add items to cart. Please try again.');
        } finally {
            addBtn.disabled = false;
            addBtn.removeAttribute('aria-busy');
            addBtn.innerHTML = originalText;
        }
    }

    async updateCart() {
        // Trigger cart update event for theme
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
        }));

        // Also try common cart drawer patterns
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            await cartDrawer.renderContents();
        }

        // Trigger cart icon update
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
                    bubbles: true,
                    detail: { cart }
                }));
            });
    }

    showSuccessMessage() {
        // Try to open cart drawer if available
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
            return;
        }

        // Fallback: show simple notification
        const notification = document.createElement('div');
        notification.className = 'product-customizer-notification';
        notification.innerHTML = `
      <div class="notification-content">
        <span>✓ Items added to cart</span>
      </div>
    `;
        document.body.appendChild(notification);

        setTimeout(() => {
            notification.classList.add('notification--visible');
        }, 100);

        setTimeout(() => {
            notification.classList.remove('notification--visible');
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    }
}

// Initialize when DOM is ready - ONLY if customizer exists on the page
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        // Only initialize if there's a customizer config on the page
        if (document.querySelector('[data-customizer-config]')) {
            new ProductCustomizer();
        }
    });
} else {
    // Only initialize if there's a customizer config on the page
    if (document.querySelector('[data-customizer-config]')) {
        new ProductCustomizer();
    }
}
